import { BOOKSTERA_READER_APP_URL } from '@/lib/readerApp';
import { ArrowRight, Smartphone, X } from 'lucide-react';
import { useState } from 'react';

const DISMISSAL_KEY = 'bookstera-reader-callout-dismissed-at';
const DISMISSAL_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

type ReaderAppCalloutProps = {
  compact?: boolean;
  className?: string;
  dismissible?: boolean;
};

export function ReaderAppCallout({ compact = false, className = '', dismissible = false }: ReaderAppCalloutProps) {
  const [visible, setVisible] = useState(() => {
    if (!dismissible || typeof window === 'undefined') return true;
    const dismissedAt = Number(window.localStorage.getItem(DISMISSAL_KEY));
    if (!dismissedAt) return true;
    if (Date.now() - dismissedAt >= DISMISSAL_DURATION_MS) {
      window.localStorage.removeItem(DISMISSAL_KEY);
      return true;
    }
    return false;
  });

  if (!visible) return null;

  const dismiss = () => {
    window.localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <div className={`relative ${className}`}>
      <a
        href={BOOKSTERA_READER_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`group flex items-center gap-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-lime-50 text-left text-slate-900 shadow-sm transition hover:border-emerald-300 hover:shadow-md ${compact ? 'p-4' : 'p-5 sm:p-6'} ${dismissible ? 'pr-12 sm:pr-14' : ''}`}
      >
        <span className={`flex shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm ${compact ? 'h-11 w-11' : 'h-14 w-14'}`}>
          <Smartphone className={compact ? 'h-5 w-5' : 'h-7 w-7'} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-black uppercase tracking-[0.16em] text-emerald-700">
            New on Android
          </span>
          <span className={`mt-0.5 block font-black leading-tight ${compact ? 'text-sm' : 'text-lg'}`}>
            Read with the Bookstera Reader App
          </span>
          {!compact && (
            <span className="mt-1 block text-sm leading-6 text-slate-600">
              Take your Bookstera library with you and enjoy your books on Android.
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-sm font-bold text-emerald-700 group-hover:text-emerald-900">
          <span className="hidden sm:inline">Get the app</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </span>
      </a>
      {dismissible && <button type="button" onClick={dismiss} aria-label="Dismiss Android reader app announcement for 30 days" className="absolute right-2 top-2 z-10 rounded-full p-2 text-slate-500 transition hover:bg-white/80 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600">
        <X className="h-4 w-4" aria-hidden="true" />
      </button>}
    </div>
  );
}
